import React, { useState } from 'react';
import {
    i18nFn,
    I18nReacher,
    resizedUrl,
    RewardGrant,
    useSpringBigStore,
    useTheme,
} from '@springbig/shared';
import { VStack, HStack, Box } from 'native-base';
import { Caption } from '~/shared/Typography';
import { useAppNavigate } from '~/pages';
import { faCoins, faGift, faGifts, faStar } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import { Swiper, SwiperSlide } from 'swiper/react';
import PaginationDot from 'react-native-animated-pagination-dot';
import { Keyboard, Mousewheel, Pagination } from 'swiper';
import { DesiredAction } from '~/pages/offer/components/OfferCard';
import Image from '../../../shared/Image';
import dayjs from 'dayjs';
import { Button, Card } from '~/shared/Theme';

type OffersProps = {
    offers: Array<RewardGrant | DesiredAction>;
    t: i18nFn;
};

export const Offers = ({ offers, t }: OffersProps) => {

    const { navigate, params } = useAppNavigate();
    const { setParamId } = useSpringBigStore();

    const [activeSlide, setActiveSlide] =
        useState(0);

    const {
        primary,
        button_primary,
        background_secondary,
        button_text,
        radius_md,
    } = useTheme();

    const bodyTextDesiredAction = (action: DesiredAction): string | I18nReacher => {

        switch (action?.type_id) {
        case 8:
            return t('general.birthday');
        case 9:
            return action?.name;
        case 10:
            return t('general.email');
        default:
            return '';
        }
    };

    return (
        <VStack space={2}>
            <HStack justifyContent={'space-between'} alignItems={'center'}>
                <HStack space={2} alignItems={'center'}>
                    <SpIcon icon={faGift} size={24} color={primary} />
                    <Caption
                        text={
                            offers?.length === 0
                                ? 'homepage.offers.noOffers'
                                : 'homepage.offers.offersAvailable'
                        }
                        fontWeight={'bold'}
                    />
                </HStack>
                <Button
                    variant={'text'}
                    modifier={'small'}
                    text={'homepage.offers.viewAll'}
                    onPress={() => navigate(`w/${params.wallet_id}/offers`)}
                />
            </HStack>
            <Card hasShadow>
                { offers?.length  === 0 ? (
                    <VStack alignItems={'center'} space={4}>
                        <Caption text={'homepage.offers.stayTuned'} bold />
                        <Box
                            p={8}
                            bg={button_primary}
                            rounded={'full'}
                            justifyContent={'center'}
                            alignItems={'center'}
                        >
                            <SpIcon icon={faGift} size={80} color={button_text} />
                        </Box>
                        <Caption
                            text={'homepage.offers.checkBack'}
                            textAlign={'center'}
                        />
                    </VStack>
                ) : (
                    <VStack>
                        <Swiper
                            spaceBetween={0}
                            navigation={false}
                            keyboard={true}
                            modules={[Pagination, Mousewheel, Keyboard]}
                            className="mySwiper"
                            style={{ pointerEvents: 'auto', width: '100%' }}
                            autoHeight={true}
                            onSlideChange={swiper => setActiveSlide(swiper.snapIndex)}
                        >
                            { offers?.map((item, index) => (

                                <SwiperSlide style={{ width: '100%', height: '100%' }} key={index}>
                                    <VStack alignItems={'center'} space={1}>
                                        <Box
                                            bg={background_secondary}
                                            rounded={radius_md}
                                            w={'120px'}
                                            h={'120px'}
                                            alignItems={'center'}
                                            justifyContent={'center'}
                                        >
                                            { item.reward?.images?.length > 0 && item?.reward?.images[0].url ? (
                                                <Image
                                                    rounded={radius_md}
                                                    w="120px"
                                                    h="120px"
                                                    resizeMode="cover"
                                                    uri={resizedUrl(item?.reward?.images[0].url, 120, 120)}
                                                />
                                            ) : (
                                                <SpIcon
                                                    color={primary}
                                                    icon={
                                                        item?.reward?.name ?
                                                            (item?._type === 'gift_card' ?
                                                                faCoins
                                                                :
                                                                faStar)
                                                            : faGifts
                                                    }
                                                    size={100}
                                                />
                                            )}
                                        </Box>
                                        <Caption textAlign="center" fontWeight="bold" fontSize={16}>
                                            { item?.reward?.name ?? item?.name }
                                        </Caption>
                                        <Caption textAlign="center" fontSize={12}>
                                            { item?.reward?.description?.substring(0, 205) ??
                                                  t('homepage.offers.desirableAction', [
                                                      bodyTextDesiredAction(item),
                                                      item?.rewards?.[0]?.name || '',
                                                  ])}
                                        </Caption>
                                        <Button
                                            text={'homepage.offers.redeem'}
                                            onPress={() => {

                                                setParamId(item.id); navigate(`w/${params.wallet_id}/offers/${item.id}`);
                                            }}
                                        />
                                        {item?.redemptions_available && (
                                            <Caption fontSize={9}>
                                                {item?.redemptions_available === -1
                                                    ? t('homepage.offers.unlimited')
                                                    : t('homepage.offers.timesUse', [item?.redemptions_available])}
                                            </Caption>
                                        )}
                                        <Caption fontSize={12} fontWeight="bold">{t('homepage.offers.expires')}</Caption>
                                        <Caption fontSize={12}>{dayjs(item?.expiration).format('MM/DD/YYYY h:mm A')}</Caption>
                                    </VStack>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        { offers?.length > 1 &&
                            <VStack alignItems="center">
                                <PaginationDot activeDotColor={primary} curPage={activeSlide} maxPage={offers?.length} />
                            </VStack>
                        }
                    </VStack>
                )}
            </Card>
        </VStack>
    );
};
